import { Skeleton } from "@/components/ui/skeleton";
import {
    DetailDocumentRowsSkeleton,
    DetailHeaderSkeleton,
    DetailKvRowsSkeleton,
    DetailRecipientRowsSkeleton,
    DetailSectionTitleSkeleton,
    DetailTabsSkeleton,
    ListPageHeaderSkeleton,
    type KvRowWidth,
} from "./shared";
import { EnvelopeTableSkeletonMobile } from "./tableSkeletons";

const ENVELOPE_DETAIL_ROWS: readonly KvRowWidth[] = [
    { labelWidth: "6.5rem", valueWidth: "14rem" },
    { labelWidth: "3.5rem", valueKind: "badge", badgeWidth: "4.5rem" },
    { labelWidth: "2.25rem", valueWidth: "16rem" },
    { labelWidth: "3.25rem", valueWidth: "16rem" },
    { labelWidth: "5.25rem", valueWidth: "11rem" },
    { labelWidth: "3.75rem", valueWidth: "11rem" },
    { labelWidth: "6.5rem", valueWidth: "11rem" },
    { labelWidth: "5.25rem", valueWidth: "11rem" },
];

const TEMPLATE_DETAIL_ROWS: readonly KvRowWidth[] = [
    { labelWidth: "6.5rem", valueWidth: "14rem" },
    { labelWidth: "5.25rem", valueWidth: "11rem" },
    { labelWidth: "6.5rem", valueWidth: "11rem" },
    { labelWidth: "5.5rem", valueWidth: "4.5rem" },
];

const USER_DETAIL_ROWS: readonly KvRowWidth[] = [
    { labelWidth: "4rem", valueWidth: "100%", valueMaxWidth: "100%" },
    { labelWidth: "3.25rem", valueKind: "badge", badgeWidth: "4.25rem" },
    { labelWidth: "4.75rem", valueWidth: "7rem" },
    { labelWidth: "7.5rem", valueWidth: "1.5rem" },
    { labelWidth: "2.75rem", valueWidth: "1.5rem" },
    { labelWidth: "3.5rem", valueWidth: "1.5rem" },
    { labelWidth: "5.25rem", valueWidth: "11rem", valueMaxWidth: "100%" },
];

const FORM_DETAIL_TAB_WIDTHS = ["5.5rem", "3rem"] as const;

type EntityDetailSkeletonProps = {
    rows: readonly KvRowWidth[];
    pageClassName?: string;
    titleClassName?: string;
    showAction?: boolean;
    recipientRowCount?: number;
    documentRowCount?: number;
};

function EntityDetailSkeleton({
    rows,
    pageClassName = "gs-envelope-detail-page gs-envelope-detail-skeleton",
    titleClassName = "gs-envelope-detail-skeleton-title",
    showAction = false,
    recipientRowCount = 1,
    documentRowCount = 3,
}: EntityDetailSkeletonProps) {
    return (
        <div className={pageClassName} aria-hidden>
            <div className="gs-envelope-detail-header">
                <DetailHeaderSkeleton titleClassName={titleClassName} showAction={showAction} bare />
            </div>
            <DetailKvRowsSkeleton rows={rows} />
            <DetailSectionTitleSkeleton />
            <DetailRecipientRowsSkeleton rowCount={recipientRowCount} />
            <DetailSectionTitleSkeleton />
            <DetailDocumentRowsSkeleton rowCount={documentRowCount} />
        </div>
    );
}

function EnvelopeDetailsSkeleton() {
    return <EntityDetailSkeleton rows={ENVELOPE_DETAIL_ROWS} />;
}

function TemplateDetailsSkeleton() {
    return (
        <div className="gs-template-detail-skeleton" aria-hidden>
            <DetailHeaderSkeleton
                titleClassName="gs-template-detail-skeleton-title"
                showAction
            />
            <DetailKvRowsSkeleton rows={TEMPLATE_DETAIL_ROWS} cardClassName="gs-list-page-card mb-4" />
            <DetailSectionTitleSkeleton />
            <DetailRecipientRowsSkeleton rowCount={1} cardClassName="gs-list-page-card mb-4" />
            <DetailSectionTitleSkeleton />
            <DetailDocumentRowsSkeleton rowCount={3} cardClassName="gs-list-page-card mb-4" />
        </div>
    );
}

function FormDetailsSkeleton() {
    return (
        <div className="gs-form-detail-page gs-form-detail-skeleton" aria-hidden>
            <DetailHeaderSkeleton
                titleClassName="gs-form-detail-skeleton-title"
                actionClassName="gs-transfer-ownership-btn-skeleton gs-transfer-ownership-btn-skeleton--detail"
            />
            <DetailTabsSkeleton tabWidths={FORM_DETAIL_TAB_WIDTHS} className="gs-page-tabs" />
            <div className="gs-form-detail-tab-panel">
                <div className="gs-list-page-card">
                    <div className="gs-list-page-card-header">
                        <ListPageHeaderSkeleton showFilter />
                    </div>
                    <EnvelopeTableSkeletonMobile rowCount={4} />
                </div>
            </div>
        </div>
    );
}

function UserDetailsSkeleton() {
    return (
        <div className="gs-user-detail-skeleton" aria-hidden>
            <DetailHeaderSkeleton
                titleClassName="gs-user-detail-skeleton-title"
                actionClassName="gs-user-detail-skeleton-action"
            />
            {/* Same flex/stack classes as ListCard so mobile skeleton matches loaded rows */}
            <div className="gs-list-page-card">
                <ul className="m-0 list-none p-0">
                    {USER_DETAIL_ROWS.map((row, index) => (
                        <li
                            key={index}
                            className="flex items-center border-b border-muted px-5 py-3 last:border-b-0 md:py-4"
                        >
                            <div className="flex w-full flex-col items-start md:flex-row md:items-center">
                                <div className="gs-user-detail-skeleton-label w-full shrink-0 md:w-1/2">
                                    <Skeleton
                                        className="gs-user-detail-skeleton-bar"
                                        style={{ width: row.labelWidth }}
                                    />
                                </div>
                                <div className="gs-user-detail-skeleton-value min-w-0 w-full md:w-1/2 md:flex-1">
                                    {row.valueKind === "badge" ? (
                                        <Skeleton
                                            className="gs-user-detail-skeleton-bar gs-user-detail-skeleton-badge"
                                            style={{ width: row.badgeWidth ?? "4.5rem" }}
                                        />
                                    ) : (
                                        <Skeleton
                                            className="gs-user-detail-skeleton-bar"
                                            style={{
                                                width: row.valueWidth ?? "11rem",
                                                maxWidth: row.valueMaxWidth ?? "100%",
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export { EnvelopeDetailsSkeleton, FormDetailsSkeleton, TemplateDetailsSkeleton, UserDetailsSkeleton };
